/**
 * Firebase: inicialización, Auth y Firestore con persistencia offline.
 *
 * Firestore mantiene su propia caché en IndexedDB y encola las escrituras
 * cuando no hay red, así que la app funciona igual en una planta sin señal.
 * Al recuperar conexión sincroniza sola.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** La misma configuración, para levantar una app secundaria al dar de alta
 *  usuarios sin perder la sesión del administrador. */
export const configFirebase = config;

/** `true` si falta configuración: la app corre en modo demostración local. */
export const SIN_CONFIGURAR = !config.apiKey || !config.projectId;

let app: FirebaseApp | null = null;
let dbRef: Firestore | null = null;
let authRef: Auth | null = null;

if (!SIN_CONFIGURAR) {
  app = initializeApp(config);

  // Caché persistente + coordinación entre pestañas.
  dbRef = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });

  authRef = getAuth(app);

  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
    connectFirestoreEmulator(dbRef, '127.0.0.1', 8080);
    connectAuthEmulator(authRef, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
}

export const db = dbRef;
export const auth = authRef;

/** Colecciones de Firestore. Un solo lugar donde están escritos los nombres. */
export const COL = {
  usuarios: 'usuarios',
  plantas: 'plantas',
  clientes: 'clientes',
  productos: 'productos',
  centrosCosto: 'centrosCosto',
  proyectos: 'proyectos',
  destinos: 'destinos',
  equipos: 'equipos',
  personas: 'personas',
  capitulos: 'capitulos',
  itemsContrato: 'itemsContrato',
  contratos: 'contratos',
  guias: 'guias',
  estadosPago: 'estadosPago',
  talonarios: 'talonarios',
  valoresUf: 'valoresUf',
  auditoria: 'auditoria',
} as const;
